import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../services/auth.service';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Button, 
  CircularProgress, 
  Alert,
  Fade,
  Avatar
} from '@mui/material';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonIcon from '@mui/icons-material/Person';
import { styled } from '@mui/material/styles';

// Styled Components
const PageContainer = styled(Box)(({ theme }) => ({
  minHeight: '80vh',
  padding: '40px 24px',
  background: '#f8f9fa',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  fontFamily: 'Outfit, Inter, sans-serif',
}));

const StyledCard = styled(Card)(({ theme }) => ({
  width: '100%',
  maxWidth: '600px',
  borderRadius: '16px',
  boxShadow: '0 8px 30px rgba(0, 0, 0, 0.05)',
  background: '#ffffff',
  border: '1px solid #e9edef',
  overflow: 'hidden',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '6px',
    background: 'linear-gradient(90deg, #ff5e62 0%, #ff9966 100%)',
  }
}));

const HeaderBox = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: '32px',
}));

const Title = styled(Typography)(({ theme }) => ({
  fontSize: '26px',
  fontWeight: 700,
  color: '#111b21',
  marginBottom: '8px',
}));

const Subtitle = styled(Typography)(({ theme }) => ({
  fontSize: '15px',
  color: '#6c757d',
  maxWidth: '480px',
  margin: '0 auto',
}));

const ActionButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(135deg, #008069 0%, #00a884 100%)',
  color: '#ffffff',
  textTransform: 'none',
  fontSize: '15px',
  fontWeight: 600,
  padding: '12px 28px',
  borderRadius: '10px',
  boxShadow: '0 4px 14px rgba(0, 128, 105, 0.25)',
  transition: 'all 0.3s ease',
  width: '100%',
  marginTop: '24px',
  '&:hover': {
    background: 'linear-gradient(135deg, #006d5a 0%, #009071 100%)',
    transform: 'translateY(-1px)',
    boxShadow: '0 6px 20px rgba(0, 128, 105, 0.35)',
  },
  '&:disabled': {
    background: '#e9edef',
    color: '#a1a8ad',
  }
}));

const UserPreviewCard = styled(Box)(({ theme }) => ({
  backgroundColor: '#f8f9fa',
  borderRadius: '12px',
  padding: '16px',
  marginTop: '24px',
  border: '1px dashed #ced4da',
  display: 'flex',
  alignItems: 'center',
  gap: '16px',
}));

export const Users = () => {
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { user, impersonateUser } = useAuth();

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        let adminToken = user?.accessToken;
        if (!adminToken) {
          const localUserStr = window.localStorage.getItem('user');
          if (localUserStr) {
            adminToken = JSON.parse(localUserStr)?.accessToken;
          }
        }
        const url = `${process.env.REACT_APP_BACKEND_API}/users`;
        const response = await axios.get(url, {
          headers: {
            'x-access-token': adminToken
          }
        });
        setUsers(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        console.error('Failed to load users list:', err);
        setError('No se pudo cargar la lista de usuarios. Verifique sus permisos de administrador.');
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, [user]);

  const handleSelectionChange = (event) => {
    setSelectedUserId(event.target.value);
  };

  const executeImpersonation = async () => {
    const selectedUser = users.find(u => u._id === selectedUserId);
    if (!selectedUser) return;

    if (window.confirm(`¿Está seguro de que desea iniciar sesión como ${selectedUser.username || selectedUser.email}?`)) {
      try {
        await impersonateUser(selectedUserId);
      } catch (err) {
        setError('Error al iniciar la sesión de impersonación.');
      }
    }
  };

  const getRoleLabel = (user) => {
    if (!user.roles || user.roles.length === 0) return 'User';
    return user.roles.map(role => {
      if (typeof role === 'string') return role;
      if (role && typeof role === 'object' && role.name) return role.name;
      return JSON.stringify(role);
    }).join(', ');
  };

  if (loading) {
    return (
      <PageContainer>
        <CircularProgress color="success" sx={{ mt: 10 }} />
        <Typography sx={{ mt: 2, color: '#6c757d' }}>Cargando usuarios...</Typography>
      </PageContainer>
    );
  }

  const selectedUser = users.find(u => u._id === selectedUserId);

  return (
    <PageContainer>
      <HeaderBox>
        <Title>Impersonar Usuario</Title>
        <Subtitle>
          Seleccione una cuenta de la lista para actuar en su nombre. Verá y operará la aplicación con sus vistas y permisos específicos.
        </Subtitle>
      </HeaderBox>

      {error && (
        <Alert severity="error" sx={{ width: '100%', maxWidth: '600px', mb: 3, borderRadius: '12px' }}>
          {error}
        </Alert>
      )}

      <StyledCard>
        <CardContent sx={{ p: 4 }}>
          <FormControl fullWidth variant="outlined">
            <InputLabel id="user-select-label">Seleccionar Usuario</InputLabel>
            <Select
              labelId="user-select-label"
              id="user-select"
              value={selectedUserId}
              onChange={handleSelectionChange}
              label="Seleccionar Usuario"
              sx={{ borderRadius: '10px' }}
            >
              <MenuItem value="" disabled>
                <em>Seleccione un usuario para iniciar sesión...</em>
              </MenuItem>
              {users.map((u) => (
                <MenuItem key={u._id} value={u._id}>
                  {u.username || u.email} ({u.email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedUser && (
            <Fade in={true} timeout={400}>
              <UserPreviewCard>
                <Avatar sx={{ bgcolor: '#008069', width: 48, height: 48 }}>
                  <PersonIcon />
                </Avatar>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#111b21' }}>
                    {selectedUser.username || selectedUser.email}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6c757d' }}>
                    Email: {selectedUser.email}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#6c757d', display: 'flex', alignItems: 'center', gap: '4px', mt: '2px' }}>
                    <AdminPanelSettingsIcon sx={{ fontSize: '16px', color: '#008069' }} />
                    Roles: {getRoleLabel(selectedUser)}
                  </Typography>
                </Box>
              </UserPreviewCard>
            </Fade>
          )}

          <ActionButton
            variant="contained"
            disabled={!selectedUserId}
            onClick={executeImpersonation}
            startIcon={<SwapHorizIcon />}
          >
            Iniciar Impersonación
          </ActionButton>
        </CardContent>
      </StyledCard>
    </PageContainer>
  );
};

export default Users;
